// src/features/task/pages/Tasks.tsx
// Task 頁面（清單版型重設計）

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useHeader } from '../../../hooks/useHeader';
import { Task, TaskCloseReason } from '../../../types/task';
import { useOnboarding, TipTooltip, useTipAutoMarker } from '../../onboarding';
import TaskDetail from '../../../components/tasks/TaskDetail';
import { ListChecks, WifiOff, Plus, CheckSquare, Maximize2 } from '../../../components/common/icons';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import ErrorState from '../../../components/common/ErrorState';
import EmptyState from '../../../components/common/EmptyState';
import { getCurrentUserId } from '../../../services/auth';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import DynamicFAB from '../../../components/common/DynamicFAB';
import { useTasks } from '../../../hooks/useTasks';
import { useClosedTasks } from '../../../hooks/useClosedTasks';
import { logEvent } from '../../../utils/analytics';
import { logError } from '../../../utils/logger';
import { useToast } from '../../../contexts/ToastContext';
import { taskService } from '..';
import { eventBus, EVENTS } from '../../../utils/eventBus';
import { useObjectives } from '../../goal/contexts/ObjectiveContext';
import TaskListItem from '../components/TaskListItem';
import TaskListToolbar, { StatusFilter, TaskSortKey, SortDirection } from '../components/TaskListToolbar';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { useOptimizeTaskContent } from '../../../hooks/useOptimizeTaskContent';
import { calculateSmartScore } from '../utils/taskSort';

const Tasks: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = getCurrentUserId();
  const { getActiveTipForPage, dismissAndShowNext } = useOnboarding();
  useTipAutoMarker();

  const activeTip = getActiveTipForPage("tasks");

  const queryParams = new URLSearchParams(location.search);
  const taskIdFromUrl = queryParams.get("taskId");

  const { tasks, visibleTasks, isLoading, error } =
    useTasks(userId);
  const closedTasksHook = useClosedTasks(userId);
  const { objectives } = useObjectives();

  // objective id → title 對照表（本地 map，不額外 fetch）
  const objectiveTitleMap = useMemo<Record<string, string>>(() => {
    return Object.fromEntries(objectives.map((o) => [o.id, o.title]));
  }, [objectives]);

  const [dropConfirmTaskId, setDropConfirmTaskId] = useState<string | null>(
    null,
  );
  const { optimizeState, requestOptimize } = useOptimizeTaskContent();
  const { showError, showToast } = useToast();
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(
    taskIdFromUrl,
  );
  const [isCreateDetailOpen, setIsCreateDetailOpen] = useState(false);
  const [createTaskTitle, setCreateTaskTitle] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("OPEN");

  const handleStatusFilterChange = useCallback(
    (status: StatusFilter) => {
      setStatusFilter(status);
      if (status === "CLOSED") {
        closedTasksHook.load();
      }
    },
    [closedTasksHook],
  );
  const [quickTaskInput, setQuickTaskInput] = useState("");

  // 排序狀態（不持久化，頁面離開即重置）
  const [sortKey, setSortKey] = useState<TaskSortKey>("smart");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");

  const isMobile = useMediaQuery("(max-width: 640px)");

  const quickTaskInputRef = useRef<HTMLInputElement>(null);

  // FAB 點擊：focus 快速輸入框（行動版）
  const handleAddTask = useCallback(() => {
    quickTaskInputRef.current?.focus();
    quickTaskInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const headerConfig = useMemo(() => ({ title: "Tasks" }), []);

  useHeader(headerConfig);

  useEffect(() => {
    setSelectedTaskId(taskIdFromUrl);
  }, [taskIdFromUrl]);

  const openCount = visibleTasks.length;
  const closedCount = closedTasksHook.tasks.length;

  // 過濾：CLOSED tab 使用獨立分頁資料來源，OPEN tab 從 visibleTasks（OPEN-only）取
  const filteredTasks = useMemo(() => {
    if (statusFilter === "CLOSED") return closedTasksHook.tasks;
    return visibleTasks;
  }, [visibleTasks, statusFilter, closedTasksHook.tasks]);

  // 排序
  const sortedTasks = useMemo(() => {
    const copy = [...filteredTasks];

    if (sortKey === "smart") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const scores = new Map(
        copy.map((t) => [t.id, calculateSmartScore(t, today)]),
      );
      copy.sort((a, b) => {
        const diff = (scores.get(b.id) ?? 0) - (scores.get(a.id) ?? 0);
        if (diff !== 0) return diff;
        // 相同分數時 createdAt DESC
        return (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0);
      });
    } else if (sortKey === "createdAt") {
      copy.sort((a, b) => {
        const ta = a.createdAt?.getTime() ?? 0;
        const tb = b.createdAt?.getTime() ?? 0;
        return sortDir === "desc" ? tb - ta : ta - tb;
      });
    } else if (sortKey === "priority") {
      // high(1) → medium(2) → low(3) → 無
      const order = (p?: string) => (p ? parseInt(p, 10) : 99);
      copy.sort((a, b) => order(a.priority) - order(b.priority));
    } else if (sortKey === "goal") {
      // 有 objectiveId 的在前（按 goal 標題排序），無的在最後（Inbox）
      copy.sort((a, b) => {
        const ga = a.objectiveId
          ? (objectiveTitleMap[a.objectiveId] ?? "")
          : "\uffff";
        const gb = b.objectiveId
          ? (objectiveTitleMap[b.objectiveId] ?? "")
          : "\uffff";
        return ga.localeCompare(gb);
      });
    }

    return copy;
  }, [filteredTasks, sortKey, sortDir, objectiveTitleMap]);

  const handleTaskClick = (taskId: string) => {
    eventBus.publish(EVENTS.UI.TASK_DETAIL_OPENED);
    navigate(`?taskId=${taskId}`, { replace: false });
  };

  const handleTaskClose = () => {
    navigate("", { replace: true });
    setSelectedTaskId(null);
  };

  // QuickActions: Drop（先顯示確認，確認後才棄置）
  const handleQuickDrop = (taskId: string) => {
    setDropConfirmTaskId(taskId);
  };

  // QuickActions: Optimize（呼叫 AI 優化，直接套用後顯示 Undo toast）
  const handleQuickOptimize = useCallback(
    async (taskId: string) => {
      const task = tasks.find((t: Task) => t.id === taskId);
      if (!task || !userId) return;

      const undo = await requestOptimize(task, userId);

      if (undo === null) {
        showError(
          "Optimization failed",
          "Could not optimize the task. Please try again.",
        );
        return;
      }

      showToast({
        type: "success",
        title: "Task updated",
        duration: 5000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await undo();
            } catch {
              showError(
                "Undo failed",
                "Could not restore the original task. Please try again.",
              );
            }
          },
        },
      });
    },
    [tasks, userId, requestOptimize, showError, showToast],
  );

  const handleConfirmDrop = async () => {
    if (!dropConfirmTaskId) return;
    const taskId = dropConfirmTaskId;
    setDropConfirmTaskId(null); // 立即關閉，不等 API
    const task = tasks.find((t: Task) => t.id === taskId);
    try {
      await taskService.updateTaskStatus(taskId, "CLOSED", userId, "dropped");
      if (task) {
        closedTasksHook.prependTask({
          ...task,
          status: "CLOSED",
          closedAt: new Date(),
          closeReason: "dropped",
        });
      }
    } catch (e) {
      logError("QuickDrop 失敗", e);
    }
  };

  const handleStatusChange = async (
    taskId: string,
    newStatus: "OPEN" | "CLOSED",
    category?: string,
    closeReason?: TaskCloseReason,
  ) => {
    try {
      await taskService.updateTaskStatus(taskId, newStatus, userId, closeReason);
      if (newStatus === "CLOSED") {
        // OPEN → CLOSED：從 OPEN list 找到 task，插入 closed list
        const task = tasks.find((t: Task) => t.id === taskId);
        if (task) {
          closedTasksHook.prependTask({
            ...task,
            status: "CLOSED",
            closedAt: new Date(),
            closeReason,
          });
        }
        logEvent("task_completed", { category: category ?? "none" });
      } else {
        // CLOSED → OPEN：從 closed list 移除
        closedTasksHook.removeTask(taskId);
      }
    } catch (e) {
      logError("任務狀態更新失敗", e);
    }
  };

  const handleSplitComplete = useCallback((taskId: string) => {
    // split 後 Firestore 已處理原始 task 狀態，不走 onStatusChange 避免重複寫入
    // 樂觀更新：立即把原始 task 顯示在 closed list
    const task = tasks.find((t: Task) => t.id === taskId);
    if (task) {
      closedTasksHook.prependTask({ ...task, status: 'CLOSED', closedAt: new Date(), closeReason: 'split' });
    }
    // 標記需要 refetch，下次切回 CLOSED tab 時以 Firestore 資料覆蓋樂觀更新
    closedTasksHook.reset();
    logEvent("task_split", { taskId });
  }, [tasks, closedTasksHook]);

  const handleDropped = useCallback((droppedTask: Task) => {
    logEvent("task_dropped", { category: droppedTask.category ?? "none" });
  }, []);

  // 快速路徑：樂觀更新 - 立即清空輸入框，背景呼叫 API
  const handleQuickCreate = useCallback(async (title: string) => {
    const trimmed = title.trim();
    if (!trimmed) return;
    setQuickTaskInput('');
    try {
      await taskService.createTask({ title: trimmed, userId });
    } catch (e) {
      logError('快速建立 task 失敗', e);
      showError('Create failed', 'Could not create task. Please try again.');
    }
  }, [userId, showError]);

  // 完整路徑：帶入標題，開啟 TaskDetail create mode
  const handleExpandCreate = useCallback((title: string) => {
    setCreateTaskTitle(title.trim());
    setIsCreateDetailOpen(true);
    setQuickTaskInput('');
  }, []);

  const handleSortChange = (key: TaskSortKey, dir: SortDirection) => {
    setSortKey(key);
    setSortDir(dir);
  };

  // Goal 分組標題（sortKey === 'goal' 時使用）
  const groupedTasks = useMemo(() => {
    if (sortKey !== "goal" && sortKey !== "priority") return null;

    const groups: { header: string; tasks: Task[] }[] = [];
    const seen = new Map<string, Task[]>();

    const priorityLabel: Record<string, string> = {
      "1": "High",
      "2": "Medium",
      "3": "Low",
    };

    for (const task of sortedTasks) {
      let key: string;
      if (sortKey === "goal") {
        key = task.objectiveId
          ? (objectiveTitleMap[task.objectiveId] ?? "Unknown Goal")
          : "Inbox";
      } else {
        key = task.priority
          ? (priorityLabel[task.priority] ?? "Other")
          : "No Priority";
      }
      if (!seen.has(key)) {
        seen.set(key, []);
        groups.push({ header: key, tasks: seen.get(key)! });
      }
      seen.get(key)!.push(task);
    }

    return groups;
  }, [sortKey, sortedTasks, objectiveTitleMap]);

  if (!userId) return null;

  const renderErrorStatus = () => {
    if (!error || tasks.length > 0) return null;
    return (
      <div className="flex items-center justify-center px-4 py-2 text-xs bg-orange-50 border-b border-orange-200">
        <div className="flex items-center text-orange-600">
          <WifiOff size={12} className="mr-1" />
          <span>Connection issues - using offline data</span>
        </div>
      </div>
    );
  };

  const renderListContent = () => {
    // CLOSED tab：使用獨立 loading 狀態
    if (statusFilter === 'CLOSED' && closedTasksHook.isLoading && closedTasksHook.tasks.length === 0) {
      return <LoadingSpinner size="sm" text="Loading closed tasks..." className="h-40" />;
    }

    if (isLoading && tasks.length === 0) {
      return <LoadingSpinner size="sm" text="Loading tasks..." className="h-40" />;
    }

    if (error && tasks.length === 0) {
      return (
        <ErrorState
          title="Connection Error"
          message={error.message || 'Failed to load tasks'}
          className="mx-4 my-6"
        />
      );
    }

    // 完全無任務（初始空狀態，只在 OPEN/ALL tab 顯示）
    if (statusFilter !== "CLOSED" && tasks.length === 0) {
      return (
        <EmptyState
          icon={CheckSquare}
          title="No Tasks Yet"
          action={{ label: 'Add Task', onClick: handleAddTask }}
        />
      );
    }

    // 有任務，但當前 filter 無結果
    if (filteredTasks.length === 0) {
      return (
        <EmptyState
          icon={CheckSquare}
          title={`No ${statusFilter.toLowerCase()} tasks`}
          action={statusFilter !== 'CLOSED' ? { label: 'Add Task', onClick: handleAddTask } : undefined}
        />
      );
    }

    return renderTaskList();
  };

  // 渲染清單（支援 Priority / Goal 分組）
  const renderTaskList = () => {
    if (groupedTasks) {
      return (
        <div className="min-h-[500px] space-y-4">
          {groupedTasks.map(({ header, tasks: groupTasks }) => (
            <div key={header}>
              <div className="px-1 py-1.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                {header}
              </div>
              <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
                {groupTasks.map((task) => (
                  <TaskListItem
                    key={task.id}
                    task={task}
                    onTaskClick={handleTaskClick}
                    onDrop={handleQuickDrop}
                    onToggleStatus={handleStatusChange}
                    onOptimize={handleQuickOptimize}
                    isOptimizing={
                      optimizeState.isLoading &&
                      optimizeState.taskId === task.id
                    }
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="min-h-[500px] mt-7">
        <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 overflow-hidden">
          {sortedTasks.map((task, index) => (
            <div key={task.id} className="relative">
              <TaskListItem
                task={task}
                onTaskClick={handleTaskClick}
                onDrop={handleQuickDrop}
                onToggleStatus={handleStatusChange}
                onOptimize={handleQuickOptimize}
                isOptimizing={
                  optimizeState.isLoading && optimizeState.taskId === task.id
                }
              />
              {/* Tip：第一個 Task 行 */}
              {index === 0 && activeTip === "tasks-card-actions" && (
                <TipTooltip
                  tipId="tasks-card-actions"
                  onClose={() =>
                    dismissAndShowNext("tasks-card-actions", "tasks")
                  }
                />
              )}
            </div>
          ))}
        </div>

        {/* Load more（CLOSED tab 分頁） */}
        {statusFilter === "CLOSED" && closedTasksHook.hasMore && (
          <div className="flex justify-center mt-4">
            <button
              onClick={closedTasksHook.loadMore}
              disabled={closedTasksHook.isLoading}
              className="btn-secondary btn-sm rounded-full"
              aria-label="Load more closed tasks"
            >
              {closedTasksHook.isLoading ? "Loading..." : "Load more"}
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full relative">
      <div className="content-standard">
          {renderErrorStatus()}

          {/* Quick Task 輸入區域 */}
          <div
            className={
              isMobile
                ? "mb-4 pb-3 border-b border-gray-200"
                : "bg-white rounded-xl p-5 border border-gray-200 mb-6"
            }
          >
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  ref={quickTaskInputRef}
                  type="text"
                  value={quickTaskInput}
                  onChange={(e) => setQuickTaskInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.nativeEvent.isComposing) return;
                    if (e.key === "Enter" && quickTaskInput.trim()) {
                      handleQuickCreate(quickTaskInput);
                    }
                  }}
                  aria-label="New task title"
                  className="w-full px-4 py-2 pr-10 border border-gray-200 rounded-lg text-base bg-gray-50 transition-all focus:outline-none focus:border-sky-600 focus:bg-white focus:ring-3 focus:ring-sky-100"
                  placeholder="What needs to be done?"
                />
                {quickTaskInput.trim() && (
                  <button
                    type="button"
                    onClick={() => handleExpandCreate(quickTaskInput)}
                    className="absolute top-1/2 -translate-y-1/2 right-2 p-1.5 text-gray-400 hover:text-sky-600 hover:bg-sky-50 rounded-md transition-all duration-200"
                    aria-label="Expand to full task form"
                    title="Add more details"
                  >
                    <Maximize2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <button
                onClick={() => handleQuickCreate(quickTaskInput)}
                disabled={!quickTaskInput.trim()}
                className="btn-accent btn-sm"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Add Task</span>
              </button>
            </div>
          </div>

          {/* Filter + Sort 工具列（永遠顯示） */}
          <TaskListToolbar
            statusFilter={statusFilter}
            onStatusChange={handleStatusFilterChange}
            openCount={openCount}
            closedCount={closedCount}
            sortKey={sortKey}
            sortDir={sortDir}
            onSortChange={handleSortChange}
          />

          {/* 清單區域 */}
          {renderListContent()}
        </div>

      {/* 移動設備上的 FAB 按鈕 */}
      <div className="md:hidden fixed right-6 bottom-24">
        <DynamicFAB
          onClick={handleAddTask}
          icon={<ListChecks size={28} />}
          color="indigo"
          ariaLabel="Add Task"
        />
      </div>

      {/* TaskDetail create mode */}
      <TaskDetail
        isOpen={isCreateDetailOpen}
        taskId={null}
        mode="create"
        initialTitle={createTaskTitle}
        onClose={() => { setIsCreateDetailOpen(false); setCreateTaskTitle(''); }}
        onStatusChange={handleStatusChange}
        onDropped={handleDropped}
      />

      {/* 任務詳情模態框（edit mode） */}
      {selectedTaskId && (
        <TaskDetail
          isOpen={!!selectedTaskId}
          taskId={selectedTaskId}
          initialTask={
            tasks.find((t: Task) => t.id === selectedTaskId) ??
            closedTasksHook.tasks.find((t: Task) => t.id === selectedTaskId)
          }
          onClose={handleTaskClose}
          onStatusChange={handleStatusChange}
          onSplitComplete={handleSplitComplete}
          onDropped={handleDropped}
        />
      )}

      <ConfirmDialog
        isOpen={dropConfirmTaskId !== null}
        type="warning"
        title="Drop this task?"
        message="This task will be dropped."
        confirmText="Drop"
        onConfirm={handleConfirmDrop}
        onCancel={() => setDropConfirmTaskId(null)}
      />
    </div>
  );
};

export default Tasks;
